import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Heading,
  Text,
  Button,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Loader,
  AlertBanner,
  AlertBannerText,
  Flex,
  Box,
  Toast,
} from '@vibe/core'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { PermissionGate } from '../components/common/PermissionGate'
import CandidateQualificationActions from '../components/common/CandidateQualificationActions'
import QualificationStatusBadge from '../components/common/QualificationStatusBadge'
import { CVViewer, AIEvaluation } from '../components/dashboard'
import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import { usePermissions } from '../hooks/usePermissions'
import type { Candidate } from '../types'
import { UserRole, QualificationStatus } from '../types'

/**
 * Candidate Detail page with tabbed interface for viewing candidate information and evaluation results
 */
const CandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Get solicitud ID from location state or URL params
  const solicitudId =
    location.state?.solicitudId ||
    new URLSearchParams(location.search).get('solicitud')

  const [candidate, setCandidate] = useState<Candidate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0)
  const [qualificationLoading, setQualificationLoading] = useState(false)

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [toastType, setToastType] = useState<'positive' | 'negative'>(
    'positive'
  )

  // Get user permissions for access control
  const { hasRole } = usePermissions()

  useEffect(() => {
    if (!id) {
      setError('ID de candidato no proporcionado')
      setLoading(false)
      return
    }

    loadCandidateDetail()
  }, [id])

  const loadCandidateDetail = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await apiService.getCandidateDetail(id!)
      setCandidate(response.data)
    } catch (err) {
      console.error('Error loading candidate detail:', err)
      setError(
        getErrorMessage(err) || 'Error al cargar los detalles del candidato'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleBackToCandidates = () => {
    if (solicitudId) {
      navigate(`/candidates/${solicitudId}`)
    } else {
      navigate(-1) // Go back to previous page
    }
  }

  const handleTabChange = (tabId: number) => {
    setActiveTab(tabId)
  }

  const handleQualificationUpdate = async (
    candidateId: string,
    status: QualificationStatus
  ) => {
    try {
      setQualificationLoading(true)

      await apiService.updateCandidateQualification(candidateId, {
        qualification_status: status as 'califica' | 'no_califica',
      })

      // Update local candidate state
      if (candidate) {
        setCandidate({
          ...candidate,
          qualification_status: status,
          qualified_at: new Date().toISOString(),
          // Note: qualified_by would be set by the backend
        })
      }

      const statusText =
        status === QualificationStatus.CALIFICA
          ? 'calificado'
          : 'marcado como no califica'
      showToast(`Candidato ${statusText} exitosamente`, 'positive')
    } catch (err) {
      console.error('Error updating qualification:', err)
      const errorMessage =
        getErrorMessage(err) || 'Error al actualizar la calificación'
      showToast(errorMessage, 'negative')
      throw err // Re-throw so the component can handle it
    } finally {
      setQualificationLoading(false)
    }
  }

  const showToast = (message: string, type: 'positive' | 'negative') => {
    setToastMessage(message)
    setToastType(type)
  }

  const closeToast = () => {
    setToastMessage(null)
  }

  // Check if Viewer has access to this candidate
  const canViewerAccessCandidate = () => {
    // Admins and Editors can access all candidates
    if (hasRole(UserRole.ADMINISTRADOR) || hasRole(UserRole.EDITOR)) {
      return true
    }

    // Viewers can only access qualified candidates
    if (hasRole(UserRole.VISUALIZADOR)) {
      return candidate?.qualification_status === 'califica'
    }

    return false
  }

  if (loading) {
    return (
      <AuthGuard>
        <Layout>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <Flex
              direction="column"
              align="center"
              justify="center"
              className="min-h-64"
            >
              <Loader size="large" />
              <Text className="mt-4">Cargando detalles del candidato...</Text>
            </Flex>
          </div>
        </Layout>
      </AuthGuard>
    )
  }

  if (error) {
    return (
      <AuthGuard>
        <Layout>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <AlertBanner backgroundColor="negative" className="mb-6">
              <AlertBannerText text={error} />
            </AlertBanner>
            <Button onClick={handleBackToCandidates} kind="secondary">
              Volver a Candidatos
            </Button>
          </div>
        </Layout>
      </AuthGuard>
    )
  }

  if (!candidate) {
    return (
      <AuthGuard>
        <Layout>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <AlertBanner backgroundColor="warning" className="mb-6">
              <AlertBannerText text="Candidato no encontrado" />
            </AlertBanner>
            <Button onClick={handleBackToCandidates} kind="secondary">
              Volver a Candidatos
            </Button>
          </div>
        </Layout>
      </AuthGuard>
    )
  }

  // Check access permissions for Viewers
  if (!canViewerAccessCandidate()) {
    return (
      <AuthGuard>
        <Layout>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <AlertBanner backgroundColor="negative" className="mb-6">
              <AlertBannerText text="No tienes permisos para ver este candidato. Solo puedes acceder a candidatos que han sido calificados." />
            </AlertBanner>
            <Button onClick={handleBackToCandidates} kind="secondary">
              Volver a Candidatos
            </Button>
          </div>
        </Layout>
      </AuthGuard>
    )
  }

  return (
    <AuthGuard>
      <Layout>
        <div className="bg-white rounded-lg shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <Flex justify="space-between" align="center" className="mb-4">
              <div>
                <Heading type="h1">
                  {candidate.nombres} {candidate.apellidos}
                </Heading>
                <Flex align="center" gap="medium" className="mt-2">
                  <QualificationStatusBadge
                    status={candidate.qualification_status || 'pendiente'}
                    size="medium"
                  />
                  {candidate.qualified_by && candidate.qualified_at && (
                    <Text type="text3" color="secondary">
                      Calificado por {candidate.qualified_by} el{' '}
                      {new Date(candidate.qualified_at).toLocaleDateString(
                        'es-ES',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )}
                    </Text>
                  )}
                </Flex>
              </div>
              <Button onClick={handleBackToCandidates} kind="secondary">
                Volver a Candidatos
              </Button>
            </Flex>

            <Text color="secondary" className="mb-2">
              {candidate.cargo}
            </Text>

            <Text type="text3" color="secondary">
              Registrado:{' '}
              {new Date(candidate.fecha_registro).toLocaleDateString('es-ES')}
            </Text>

            {/* Qualification Actions */}
            <PermissionGate
              requiredRoles={[UserRole.ADMINISTRADOR, UserRole.EDITOR]}
              requireAll={false}
            >
              <Box className="mt-4 p-4 bg-gray-50 rounded-lg">
                <CandidateQualificationActions
                  candidate={candidate}
                  onQualificationUpdate={handleQualificationUpdate}
                  loading={qualificationLoading}
                />
              </Box>
            </PermissionGate>
          </div>

          {/* Tabbed Content */}
          <Box className="p-6">
            <TabList
              activeTabId={activeTab}
              onTabChange={handleTabChange}
              tabType="Compact"
              stretchedUnderline
              className="mb-6"
            >
              <Tab value={0}>Evaluación de Inteligencia Artificial</Tab>
              <Tab
                value={1}
                disabled={!candidate.cv_filename && !candidate.cv_s3_key}
              >
                CV del Candidato
              </Tab>
            </TabList>

            <TabPanels activeTabId={activeTab}>
              <TabPanel index={0}>
                <AIEvaluation candidate={candidate} />
              </TabPanel>

              <TabPanel index={1}>
                {candidate.cv_filename || candidate.cv_s3_key ? (
                  <CVViewer
                    candidateId={candidate.id_postulacion}
                    cvUrl={candidate.url_visor}
                    filename={candidate.cv_filename}
                  />
                ) : (
                  <AlertBanner backgroundColor="warning">
                    <AlertBannerText text="No hay CV disponible para este candidato" />
                  </AlertBanner>
                )}
              </TabPanel>
            </TabPanels>
          </Box>
        </div>

        {/* Toast Notification */}
        {toastMessage && (
          <Toast
            open={!!toastMessage}
            type={toastType}
            onClose={closeToast}
            autoHideDuration={5000}
            className="fixed top-4 right-4 z-50"
          >
            {toastMessage}
          </Toast>
        )}
      </Layout>
    </AuthGuard>
  )
}

export default CandidateDetail
